import { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Clock,
  Check,
  X,
  User,
  Calendar,
  Cog,
  AlertTriangle,
  Loader2,
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Package,
  RotateCcw,
  RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PendingApprovals = () => {
  const navigate = useNavigate();
  const [pendingBookings, setPendingBookings] = useState([]);
  const [pendingReturnRequests, setPendingReturnRequests] = useState([]);
  const [pendingRentalReservations, setPendingRentalReservations] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedBookings, setSelectedBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedWorkerId, setSelectedWorkerId] = useState("");
  
  // Bulk action state
  const [bulkActionDialogOpen, setBulkActionDialogOpen] = useState(false);
  const [bulkAction, setBulkAction] = useState(null); // 'confirm' or 'decline'
  const [bulkWorkerId, setBulkWorkerId] = useState("");

  const fetchData = async () => {
    try {
      const [bookingsRes, usersRes, returnReqsRes, rentalResvRes] = await Promise.all([
        axios.get(`${API}/service-bookings`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/rental-return-requests/pending`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/rentals/pending-approval`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      
      // Filter for pending bookings only
      const pending = (bookingsRes.data || [])
        .filter(b => b.status === "pending")
        .sort((a, b) => a.booking_date.localeCompare(b.booking_date));
      
      setPendingBookings(pending);
      setPendingReturnRequests(returnReqsRes.data || []);
      setPendingRentalReservations(rentalResvRes.data || []);
      setWorkers(usersRes.data.filter(u => u.role === "worker" || u.role === "manager"));
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load pending approvals");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleAction = async (bookingId, action) => {
    setActionLoading(true);
    try {
      await axios.post(
        `${API}/service-bookings/${bookingId}/${action}`,
        {},
        { withCredentials: true }
      );
      toast.success(action === "confirm" ? "Booking confirmed" : "Booking declined");
      setDetailDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} booking`);
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssignWorker = async (bookingId, workerId) => {
    if (!workerId) return;
    try {
      await axios.post(
        `${API}/service-bookings/${bookingId}/assign-worker?worker_id=${workerId}`,
        {},
        { withCredentials: true }
      );
      toast.success("Technician assigned");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign technician");
    }
  };

  const toggleSelectBooking = (bookingId) => {
    setSelectedBookings(prev =>
      prev.includes(bookingId)
        ? prev.filter(id => id !== bookingId)
        : [...prev, bookingId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedBookings.length === pendingBookings.length) {
      setSelectedBookings([]);
    } else {
      setSelectedBookings(pendingBookings.map(b => b.booking_id));
    }
  };

  const handleBulkAction = async () => {
    if (selectedBookings.length === 0) return;
    
    setActionLoading(true);
    let successCount = 0;
    let errorCount = 0;

    for (const bookingId of selectedBookings) {
      try {
        // Assign worker if selected (for confirm action only)
        if (bulkAction === "confirm" && bulkWorkerId && bulkWorkerId !== "none") {
          await axios.post(
            `${API}/service-bookings/${bookingId}/assign-worker?worker_id=${bulkWorkerId}`,
            {},
            { withCredentials: true }
          );
        }
        
        // Execute action
        await axios.post(
          `${API}/service-bookings/${bookingId}/${bulkAction === "confirm" ? "confirm" : "cancel"}`,
          {},
          { withCredentials: true }
        );
        successCount++;
      } catch (error) {
        errorCount++;
        console.error(`Failed to process booking ${bookingId}:`, error);
      }
    }

    setBulkActionDialogOpen(false);
    setSelectedBookings([]);
    setBulkWorkerId("");
    
    if (successCount > 0) {
      toast.success(`${successCount} booking(s) ${bulkAction === "confirm" ? "confirmed" : "declined"}`);
    }
    if (errorCount > 0) {
      toast.error(`Failed to process ${errorCount} booking(s)`);
    }
    
    setActionLoading(false);
    fetchData();
  };

  const getDaysWaiting = (createdAt) => {
    if (!createdAt) return 0;
    const created = new Date(createdAt);
    const now = new Date();
    return Math.floor((now - created) / (1000 * 60 * 60 * 24));
  };

  const getUrgencyBadge = (daysWaiting) => {
    if (daysWaiting >= 7) {
      return <Badge className="rounded-sm bg-red-100 text-red-700"><AlertTriangle className="h-3 w-3 mr-1" />Urgent</Badge>;
    }
    if (daysWaiting >= 3) {
      return <Badge className="rounded-sm bg-amber-100 text-amber-700"><Clock className="h-3 w-3 mr-1" />{daysWaiting}d waiting</Badge>;
    }
    return <Badge className="rounded-sm bg-slate-100 text-slate-600">{daysWaiting}d waiting</Badge>;
  };

  if (loading) {
    return (
      <div className="p-6 md:p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-slate-200 rounded w-48"></div>
          <div className="h-96 bg-slate-200 rounded-sm"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 md:p-8" data-testid="pending-approvals-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-3xl font-semibold text-slate-900 tracking-tight">
            Pending Approvals
          </h1>
          <p className="text-slate-500 mt-1">
            Review and approve customer booking requests
          </p>
        </div>
        
        {selectedBookings.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">{selectedBookings.length} selected</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setBulkAction("decline");
                setBulkActionDialogOpen(true);
              }}
              className="rounded-sm text-red-600 hover:text-red-700"
            >
              <X className="h-4 w-4 mr-1" />
              Decline Selected
            </Button>
            <Button
              size="sm"
              onClick={() => {
                setBulkAction("confirm");
                setBulkActionDialogOpen(true);
              }}
              className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
            >
              <Check className="h-4 w-4 mr-1" />
              Confirm Selected
            </Button>
          </div>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Pending Bookings</p>
                <p className="text-2xl font-semibold">{pendingBookings.length}</p>
              </div>
              <div className="p-2 bg-amber-50 rounded-sm">
                <ClipboardList className="h-5 w-5 text-amber-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Waiting 7+ Days</p>
                <p className="text-2xl font-semibold text-red-600">
                  {pendingBookings.filter(b => getDaysWaiting(b.created_at) >= 7).length}
                </p>
              </div>
              <div className="p-2 bg-red-50 rounded-sm">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Bookings This Week</p>
                <p className="text-2xl font-semibold">
                  {pendingBookings.filter(b => {
                    const bookingDate = new Date(b.booking_date);
                    const today = new Date();
                    const weekFromNow = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000);
                    return bookingDate >= today && bookingDate <= weekFromNow;
                  }).length}
                </p>
              </div>
              <div className="p-2 bg-blue-50 rounded-sm">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Rental Reservations</p>
                <p className="text-2xl font-semibold text-blue-600">{pendingRentalReservations.length}</p>
              </div>
              <div className="p-2 bg-blue-50 rounded-sm">
                <Package className="h-5 w-5 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-sm border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Return Requests</p>
                <p className="text-2xl font-semibold text-purple-600">{pendingReturnRequests.length}</p>
              </div>
              <div className="p-2 bg-purple-50 rounded-sm">
                <Package className="h-5 w-5 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Bookings Table */}
      <Card className="rounded-sm border-slate-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Pending Booking Requests</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {pendingBookings.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedBookings.length === pendingBookings.length && pendingBookings.length > 0}
                      onCheckedChange={toggleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Date & Time</TableHead>
                  <TableHead>Waiting</TableHead>
                  <TableHead>Assign Technician</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingBookings.map((booking) => {
                  const daysWaiting = getDaysWaiting(booking.created_at);
                  return (
                    <TableRow 
                      key={booking.booking_id}
                      className={daysWaiting >= 7 ? "bg-red-50/50" : ""}
                    >
                      <TableCell>
                        <Checkbox
                          checked={selectedBookings.includes(booking.booking_id)}
                          onCheckedChange={() => toggleSelectBooking(booking.booking_id)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center">
                            <User className="h-4 w-4 text-slate-500" />
                          </div>
                          <div>
                            <p className="font-medium">{booking.customer_name}</p>
                            <p className="text-xs text-slate-500">{booking.installation_name}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Cog className="h-4 w-4 text-slate-400" />
                          <span>{booking.service_name || "General Service"}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{booking.booking_date}</p>
                          <p className="text-xs text-slate-500">
                            {booking.time_slot?.start_time} - {booking.time_slot?.end_time}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getUrgencyBadge(daysWaiting)}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={booking.assigned_worker_id || ""}
                          onValueChange={(value) => handleAssignWorker(booking.booking_id, value)}
                        >
                          <SelectTrigger className="w-40 h-8 rounded-sm text-xs">
                            <SelectValue placeholder="Select technician" />
                          </SelectTrigger>
                          <SelectContent>
                            {workers.map((worker) => (
                              <SelectItem key={worker.user_id} value={worker.user_id}>
                                {worker.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAction(booking.booking_id, "cancel")}
                            disabled={actionLoading}
                            className="rounded-sm text-red-600 hover:text-red-700 h-8"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleAction(booking.booking_id, "confirm")}
                            disabled={actionLoading}
                            className="rounded-sm bg-emerald-600 hover:bg-emerald-700 h-8"
                          >
                            <Check className="h-3.5 w-3.5 mr-1" />
                            Confirm
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-16">
              <CheckCircle2 className="h-12 w-12 text-emerald-300 mx-auto mb-3" />
              <p className="text-slate-500 text-lg font-medium">All caught up!</p>
              <p className="text-slate-400 text-sm mt-1">No pending booking requests</p>
              <Button
                variant="outline"
                onClick={() => navigate("/service-calendar")}
                className="mt-4 rounded-sm"
              >
                View Service Calendar
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pending Return Requests Table */}
      {pendingReturnRequests.length > 0 && (
        <Card className="rounded-sm border-slate-200 mt-6">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-purple-600" />
              <CardTitle className="text-lg">Pending Return/Replacement Requests</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Rental Item</TableHead>
                  <TableHead>Request Type</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingReturnRequests.map((req) => (
                  <TableRow key={req.request_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-purple-500" />
                        </div>
                        <span className="font-medium">{req.customer_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{req.item_name}</p>
                        <code className="text-xs text-slate-400">{req.rental_id}</code>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge className={`rounded-sm ${req.request_type === "replacement" ? "bg-blue-100 text-blue-700" : "bg-amber-100 text-amber-700"}`}>
                        {req.request_type === "replacement" ? (
                          <><RefreshCw className="h-3 w-3 mr-1" />Replacement</>
                        ) : (
                          <><RotateCcw className="h-3 w-3 mr-1" />Return</>
                        )}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[200px] truncate">
                      {req.reason || "—"}
                    </TableCell>
                    <TableCell>
                      {new Date(req.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate("/rental-management")}
                        className="rounded-sm"
                      >
                        Review
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Pending Rental Reservations Table */}
      {pendingRentalReservations.length > 0 && (
        <Card className="rounded-sm border-slate-200 mt-6">
          <CardHeader className="pb-3">
            <div className="flex items-center gap-2">
              <Package className="h-5 w-5 text-blue-600" />
              <CardTitle className="text-lg">Pending Rental Reservations</CardTitle>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Customer</TableHead>
                  <TableHead>Rental Item</TableHead>
                  <TableHead>Rental Period</TableHead>
                  <TableHead>Quantity</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingRentalReservations.map((rental) => (
                  <TableRow key={rental.rental_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                          <User className="h-4 w-4 text-blue-500" />
                        </div>
                        <span className="font-medium">{rental.customer_name}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{rental.item_name}</p>
                        <code className="text-xs text-slate-400">{rental.rental_id}</code>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm">
                        <p>{rental.start_date}</p>
                        <p className="text-slate-500">to {rental.end_date}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{rental.quantity}</TableCell>
                    <TableCell>
                      {new Date(rental.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-2 justify-end">
                        <Button
                          size="sm"
                          className="rounded-sm bg-emerald-600 hover:bg-emerald-700"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/rentals/${rental.rental_id}/confirm`, {}, { withCredentials: true });
                              toast.success("Rental confirmed");
                              fetchData();
                            } catch (error) {
                              toast.error("Failed to confirm rental");
                            }
                          }}
                        >
                          <Check className="h-3 w-3 mr-1" />
                          Confirm
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="rounded-sm text-red-600 hover:bg-red-50"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/rentals/${rental.rental_id}/decline`, { reason: "Declined by admin" }, { withCredentials: true });
                              toast.success("Rental declined");
                              fetchData();
                            } catch (error) {
                              toast.error("Failed to decline rental");
                            }
                          }}
                        >
                          <X className="h-3 w-3 mr-1" />
                          Decline
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Bulk Action Dialog */}
      <Dialog open={bulkActionDialogOpen} onOpenChange={setBulkActionDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {bulkAction === "confirm" ? "Confirm Selected Bookings" : "Decline Selected Bookings"}
            </DialogTitle>
            <DialogDescription>
              {bulkAction === "confirm"
                ? `You are about to confirm ${selectedBookings.length} booking(s). This will notify the customers.`
                : `You are about to decline ${selectedBookings.length} booking(s). This action cannot be undone.`}
            </DialogDescription>
          </DialogHeader>
          
          {bulkAction === "confirm" && (
            <div className="space-y-4 py-4">
              <div>
                <Label>Assign Technician (Optional)</Label>
                <Select value={bulkWorkerId} onValueChange={setBulkWorkerId}>
                  <SelectTrigger className="rounded-sm mt-2">
                    <SelectValue placeholder="Select technician for all bookings" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No technician assigned</SelectItem>
                    {workers.map((worker) => (
                      <SelectItem key={worker.user_id} value={worker.user_id}>
                        {worker.name} ({worker.role === "worker" ? "Technician" : "Manager"})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setBulkActionDialogOpen(false)}
              className="rounded-sm"
            >
              Cancel
            </Button>
            <Button
              onClick={handleBulkAction}
              disabled={actionLoading}
              className={`rounded-sm ${bulkAction === "confirm" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}`}
            >
              {actionLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : bulkAction === "confirm" ? (
                <Check className="h-4 w-4 mr-2" />
              ) : (
                <X className="h-4 w-4 mr-2" />
              )}
              {bulkAction === "confirm" ? "Confirm All" : "Decline All"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PendingApprovals;
